import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ParseKeys } from 'i18next';
import {
  HttpError,
  type MiceNeedView,
  type MiceChangeDateRequest,
  type MiceChooseInput,
  type MiceNeedsOrderRequest,
  type MiceNeedsOrderResult,
} from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import type { MiceEmailRow, MiceOrderTarget } from '@web/data/repositories/mice/mice_repository';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { RECOMMEND_BATCH, chunks } from '../travelers/eligibility';
import { errorMessage } from './use_mice_orders';

/** 409 `premises_unchecked`: alguém salvou as premissas sem conferir depois que a tela carregou. */
const premisesUnchecked = (err: HttpError): boolean =>
  err.status === 409 &&
  (err.details?.details as { code?: unknown } | undefined)?.code === 'premises_unchecked';

/** 503 = recomendação desligada no servidor (sem LLM); o texto do servidor nunca vai para a tela. */
function recommendErrorMessage(err: unknown): string {
  if (err instanceof HttpError && err.status === 503) return t('mice.needs.recommendUnavailable');
  if (err instanceof HttpError && premisesUnchecked(err)) return t('mice.travelers.premisesFirst');
  return errorMessage(err);
}

/** A recusa da correção do e-mail na língua da tela: e-mail de outra linha do evento ou a linha
 *  que mudou desde a leitura. */
function emailFixErrorMessage(err: unknown): string {
  if (!(err instanceof HttpError)) return errorMessage(err);
  const code = (err.details?.details as { code?: unknown } | undefined)?.code;
  if (err.status === 409 && code === 'email_taken') return t('mice.emailFix.taken');
  if (err.status === 409) return t('mice.emailFix.stale');
  return errorMessage(err);
}

type IdsAction = 'recommend' | 'approve' | 'manual' | 'queue' | 'unqueue' | 'release' | 'remove';

const DONE_KEY = {
  recommend: 'mice.needs.actions.recommend.done',
  approve: 'mice.needs.actions.approve.done',
  manual: 'mice.needs.actions.manual.done',
  queue: 'mice.actions.queue.done',
  unqueue: 'mice.actions.unqueue.done',
  release: 'mice.actions.release.done',
  remove: 'mice.actions.remove.done',
} as const satisfies Record<IdsAction, ParseKeys>;

/** Parte dos lotes de recomendação já foi aceita quando um deles falhou. */
class PartialRecommendError extends Error {
  constructor(
    readonly done: number,
    readonly total: number,
    override readonly cause: unknown,
  ) {
    super('recomendação parcial');
  }
}

function orderMessage(result: MiceNeedsOrderResult, target: MiceOrderTarget): string {
  if ('allGreen' in target) return t('mice.status.emitGreenDone', { count: result.ordered });
  const ordered = t('mice.needs.order.ordered', { count: result.ordered });
  return result.skipped > 0
    ? `${ordered} · ${t('mice.needs.order.skipped', { count: result.skipped })}`
    : ordered;
}

/** "Emitir os verdes" vai em lotes no servidor: repete enquanto faltam verdes e o lote andou (um
 *  lote que não emitiu nada pararia no mesmo lugar), somando as emitidas. */
async function orderAll(
  orgId: string,
  projectId: string,
  target: MiceOrderTarget,
): Promise<MiceNeedsOrderResult> {
  if (!('allGreen' in target)) return miceRepository.orderNeeds(orgId, projectId, target);
  let ordered = 0;
  for (;;) {
    const batch = await miceRepository.orderNeeds(orgId, projectId, target);
    ordered += batch.ordered;
    if (batch.remaining === 0 || batch.ordered === 0) return { ...batch, ordered };
  }
}

/** Teto de `pageSize`/`needIds` no servidor: "comprar todos os aprovados" leva até 200. */
const ORDER_ALL_LIMIT = 200;
const APPROVED = { page: 1, pageSize: ORDER_ALL_LIMIT, status: 'approved' as const };

/** Aprovadas do projeto para a faixa "N aprovados": as mesmas (até 200) que a compra leva. */
export function useApprovedNeeds(projectId: string): MiceNeedView[] {
  const { orgId } = useOrgContext();
  const query = useQuery({
    queryKey: queryKeys.mice.needs(orgId, projectId, APPROVED),
    queryFn: () => miceRepository.listNeeds(orgId, projectId, APPROVED),
  });
  return (query.data?.items ?? []).filter((n) => n.status === 'approved');
}

/** O que o operador decide numa necessidade aberta: pedir revisão, escolher voos, mudar a data. */
function useNeedDecisions(projectId: string, refresh: () => Promise<void>) {
  const { orgId } = useOrgContext();
  const { showSnackbar } = useSnackbar();

  const reconsider = useMutation({
    mutationFn: ({ needId, comment }: { needId: string; comment: string }) =>
      miceRepository.reconsiderNeed(orgId, projectId, needId, comment),
    onSuccess: async () => {
      await refresh();
      showSnackbar({ severity: 'success', message: t('mice.needs.reconsider.done') });
    },
    onError: (err) => {
      // Outro 409 = `ordered` com pedido em andamento: sair dela abriria outra compra do trecho.
      const message =
        err instanceof HttpError && err.status === 409 && !premisesUnchecked(err)
          ? t('mice.needs.reconsider.liveOrder')
          : recommendErrorMessage(err);
      showSnackbar({ severity: 'error', message });
    },
    meta: { silent: true },
  });

  const choose = useMutation({
    mutationFn: ({ needId, choice }: { needId: string; choice: MiceChooseInput }) =>
      miceRepository.chooseFlight(orgId, projectId, needId, choice),
    onSuccess: async () => {
      await refresh();
      showSnackbar({ severity: 'success', message: t('mice.needs.choose.done') });
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: recommendErrorMessage(err) });
    },
    meta: { silent: true },
  });

  const changeDate = useMutation({
    mutationFn: ({ needId, change }: { needId: string; change: MiceChangeDateRequest }) =>
      miceRepository.changeNeedDate(orgId, projectId, needId, change),
    onSuccess: async () => {
      await refresh();
      showSnackbar({ severity: 'success', message: t('mice.needs.dates.done') });
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: recommendErrorMessage(err) });
    },
    meta: { silent: true },
  });

  return {
    reconsider: (needId: string, comment: string, onDone: () => void) => {
      reconsider.mutate({ needId, comment }, { onSuccess: onDone });
    },
    reconsidering: reconsider.isPending,
    /** Uma chamada com todos os sentidos trocados; `approve` já aprova depois de cotar. */
    choose: (needId: string, choice: MiceChooseInput) => {
      choose.mutate({ needId, choice });
    },
    choosing: choose.isPending,
    /** Nova data de um sentido; `onDone` fecha o campo quando o servidor aceita. */
    changeDate: (needId: string, change: MiceChangeDateRequest, onDone: () => void) => {
      changeDate.mutate({ needId, change }, { onSuccess: onDone });
    },
    changingDate: changeDate.isPending,
  };
}

/** Ações sobre viajantes do evento; toda ação atualiza o projeto inteiro (contadores e lista). */
export function useTravelerActions(projectId: string) {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: queryKeys.mice.project(orgId, projectId) });
  const decisions = useNeedDecisions(projectId, refresh);

  /** O servidor aceita até 10 por pedido: lotes em série, a soma no fim. Um lote que falha depois
   *  de outros aceitos vira `PartialRecommendError`, para a tela dizer quantas já foram. */
  const recommendInBatches = async (ids: string[]): Promise<number> => {
    let done = 0;
    for (const batch of chunks(ids, RECOMMEND_BATCH)) {
      try {
        done += await miceRepository.recommendNeeds(orgId, projectId, batch);
      } catch (cause: unknown) {
        if (done === 0) throw cause;
        throw new PartialRecommendError(done, ids.length, cause);
      }
    }
    return done;
  };

  const run = (name: IdsAction, ids: string[]): Promise<number> => {
    switch (name) {
      case 'recommend':
        return recommendInBatches(ids);
      case 'approve':
        return miceRepository.approveNeeds(orgId, projectId, ids);
      case 'manual':
        return miceRepository.markNeedsManual(orgId, projectId, ids);
      case 'queue':
      case 'release':
      case 'remove':
      case 'unqueue':
      default:
        return miceRepository[name](orgId, projectId, ids);
    }
  };

  const action = useMutation({
    mutationFn: ({ name, ids }: { name: IdsAction; ids: string[]; onDone?: () => void }) =>
      run(name, ids),
    onSuccess: async (updated, { name, onDone }) => {
      onDone?.();
      await refresh();
      showSnackbar({ severity: 'success', message: t(DONE_KEY[name], { count: updated }) });
    },
    onError: async (err, { name, onDone }) => {
      if (err instanceof PartialRecommendError) {
        onDone?.();
        await refresh();
        const partial = t('mice.needs.actions.recommend.partial', {
          done: err.done,
          total: err.total,
        });
        showSnackbar({
          severity: 'error',
          message: `${partial} · ${recommendErrorMessage(err.cause)}`,
        });
        return;
      }
      showSnackbar({
        severity: 'error',
        message: name === 'recommend' ? recommendErrorMessage(err) : errorMessage(err),
      });
    },
    meta: { silent: true },
  });

  const order = useMutation({
    mutationFn: ({ target }: { target: MiceOrderTarget; onDone?: () => void }) =>
      orderAll(orgId, projectId, target),
    onSuccess: async (result, { target, onDone }) => {
      onDone?.();
      await refresh();
      showSnackbar({ severity: 'success', message: orderMessage(result, target) });
    },
    onError: async (err) => {
      showSnackbar({ severity: 'error', message: errorMessage(err) });
      // Um lote do meio pode falhar depois de outros emitidos: a lista mostra o que já foi.
      await refresh();
    },
    meta: { silent: true },
  });

  const emailFix = useMutation({
    mutationFn: ({ row, email }: { row: MiceEmailRow; email: string }) =>
      miceRepository.correctEmail(orgId, projectId, row, email),
    onSuccess: async () => {
      await refresh();
      showSnackbar({ severity: 'success', message: t('mice.emailFix.done') });
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: emailFixErrorMessage(err) });
    },
    meta: { silent: true },
  });

  const ids =
    (name: IdsAction) =>
    (list: string[], onDone?: () => void): void => {
      action.mutate({ name, ids: list, onDone });
    };

  /** Todos os aprovados do projeto, lidos do servidor na hora (a compra nunca parte do cache). */
  const fetchApproved = async (): Promise<MiceNeedView[]> => {
    try {
      const page = await queryClient.fetchQuery({
        queryKey: queryKeys.mice.needs(orgId, projectId, APPROVED),
        queryFn: () => miceRepository.listNeeds(orgId, projectId, APPROVED),
        staleTime: 0,
      });
      return page.items.filter((n) => n.status === 'approved');
    } catch (err: unknown) {
      showSnackbar({ severity: 'error', message: errorMessage(err) });
      return [];
    }
  };

  return {
    fetchApproved,
    recommend: ids('recommend'),
    approve: ids('approve'),
    manual: ids('manual'),
    queue: ids('queue'),
    unqueue: ids('unqueue'),
    release: ids('release'),
    remove: ids('remove'),
    order: (needIds: string[], skip: MiceNeedsOrderRequest['skip'], onDone?: () => void) => {
      order.mutate({ target: { needIds, skip }, onDone });
    },
    /** Todas as verdes do evento, lidas pelo servidor na hora (não só as linhas carregadas). */
    orderGreen: (onDone?: () => void) => {
      order.mutate({ target: { allGreen: true }, onDone });
    },
    /** E-mail corrigido da linha sem viajante; `onDone` fecha o campo quando o servidor aceita. */
    correctEmail: (row: MiceEmailRow, email: string, onDone: () => void) => {
      emailFix.mutate({ row, email }, { onSuccess: onDone });
    },
    correctingEmail: emailFix.isPending,
    ...decisions,
    busy: action.isPending || order.isPending,
  };
}
